import {Box, Input, Stack, Tooltip, Typography} from "@mui/material";
import React, {useEffect, useState, forwardRef} from "react";
import {useDebounce} from "../../../../hooks/useDebounce";
import Chart from "react-apexcharts";
import {blue, green, grey, purple} from "@mui/material/colors";
import {useDashboard} from "../../../../context/DashboardContext";
import moment from "moment";
import DatePicker from "react-datepicker";

const RangeInput = forwardRef(({value, onClick, placeholder}, ref) => (
    <Input
        disableUnderline
        sx={{
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            fontSize: 13,
            bgcolor: grey[100],
            cursor: "pointer",
            maxWidth: 140,
            "&:hover": {bgcolor: grey[200]}
        }}
        value={value}
        placeholder={placeholder}
        onClick={onClick}
        inputRef={ref}
        readOnly
    />
));

export const TagsUsageChart = ({project}) => {
    const {tagUsage, updatedValue, refreshTagsUsage, dateRange: globalDateRange} = useDashboard();
    const debouncedValue = useDebounce(updatedValue, 3000);

    const [startDate, setStartDate] = useState(() => globalDateRange?.start || moment().startOf("day"));
    const [endDate, setEndDate] = useState(() => globalDateRange?.end || moment().endOf("day"));
    const [loading, setLoading] = useState(true);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

    // Keep the local picker in sync whenever the dashboard-wide date range changes.
    // The user can still pick a different range locally afterwards — this only pushes updates one-way (global -> local).
    useEffect(() => {
        setStartDate(globalDateRange?.start || null);
        setEndDate(globalDateRange?.end || null);
    }, [globalDateRange]);

    useEffect(() => {
        if (project?.id && startDate && endDate) {
            if (!hasLoadedOnce) setLoading(true);
            refreshTagsUsage(startDate, endDate).finally(() => {
                setLoading(false);
                setHasLoadedOnce(true);
            });
        }
    }, [project?.id, debouncedValue, startDate, endDate, refreshTagsUsage]);

    // Process chart data
    const chartLabels = [];
    const chartValues = [];

    for (const key of Object.keys(tagUsage).sort()) {
        if (tagUsage[key]) {
            chartLabels.push(key);
            chartValues.push(tagUsage[key]);
        }
    }

    const hasData = chartLabels.length > 0;

    const mainMenuCount = tagUsage?.main_menu || 0;
    const appointmentCount = tagUsage?.create_appointment || 0;
    const conversionRate = mainMenuCount > 0 ? (appointmentCount / mainMenuCount) * 100 : 0;

    const chartOptions = {
        chart: {type: 'bar', toolbar: {show: false}},
        colors: [blue[400]],
        plotOptions: {
            bar: {
                borderRadius: 4,
                horizontal: false,
                columnWidth: "50%",
                dataLabels: {
                    position: "top",
                },
            }
        },
        dataLabels: {
            enabled: true,
            offsetY: -22,
            style: {
                colors: [grey[900]],
                fontWeight: 600,
                fontSize: "12px"
            },
        },
        xaxis: {
            categories: chartLabels,
            labels: {
                rotate: 0,
                style: {colors: grey[500], fontSize: "11px"},
                trim: true,
                hideOverlappingLabels: true,
            },
            axisBorder: {show: false},
            axisTicks: {show: false},
        },
        yaxis: {
            labels: {
                style: {colors: grey[400], fontSize: "11px"}
            }
        },
        grid: {
            borderColor: grey[200],
            strokeDashArray: 3,
            padding: {top: 20},
        },
    };

    const chartSeries = [{name: 'Usage', data: chartValues}];

    return (
        <Stack direction="column" gap={0}
               justifyContent={"space-between"}
               sx={{borderRadius: 3, flex: 1, p: 0, height: "calc(100% - 0px)"}}
               className={"border-shadow"}>
            <Stack direction="row" alignItems="center" justifyContent="space-between"
                   sx={{px: 2, pt: 2, pb: 1, gap: 1}}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                    <Typography variant={"body1"} color={grey[500]}>TAGS USAGE</Typography>
                    {/*{mainMenuCount > 0 && (*/}
                    {/*    // <Tooltip title={"Successfully booked"}>*/}
                    {/*    //     <Box sx={{*/}
                    {/*    //         bgcolor: green[50], color: green[700],*/}
                    {/*    //         px: 1.25, py: 0.4, borderRadius: 2,*/}
                    {/*    //         fontSize: 12, fontWeight: 600*/}
                    {/*    //     }}>*/}
                    {/*    //         {conversionRate.toFixed(1)}%*/}
                    {/*    //     </Box>*/}
                    {/*    // </Tooltip>*/}
                    {/*)}*/}
                </Stack>
                <Box sx={{flexShrink: 0}}>
                    <DatePicker
                        selectsRange
                        placeholderText={"Date Range"}
                        startDate={startDate?.toDate()}
                        endDate={endDate?.toDate()}
                        maxDate={new Date()}
                        dateFormat={"dd MMM"}
                        customInput={<RangeInput placeholder="Date Range"/>}
                        popperPlacement="bottom-end"
                        isClearable
                        onChange={(update) => {
                            const [start, end] = update;
                            setStartDate(start ? moment(start).startOf("day") : null);
                            setEndDate(end ? moment(end).endOf("day") : null);
                        }}
                    />
                </Box>
            </Stack>
            {loading ? (
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", height: 300}}>
                    <Typography color={grey[400]}>Loading...</Typography>
                </Box>
            ) : hasData ? (
                <Chart options={chartOptions} series={chartSeries} type="bar" height={300}/>
            ) : (
                <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    height: 300, color: grey[500], flexDirection: "column", gap: 1
                }}>
                    <Typography variant="body1" color={grey[500]}>No tags data available</Typography>
                    <Typography variant="body2" color={grey[400]}>Try selecting a different date range</Typography>
                </Box>
            )}
        </Stack>
    );
};